/**
 * Pure half of `useAvatars` — no React, no Apollo, so `scripts/check-avatars.mjs` can import it
 * under plain node.
 *
 * `userAvatars` lists every user with a READY avatar as `{ email, url, expiresAt }`. The api signs
 * each URL for one hour at the top of the hour, so the URL is identical all hour long: the right
 * moment to refetch is just AFTER the earliest `expiresAt`. Refetching "5 minutes before" would get
 * the very same URL back and schedule itself again — a loop.
 */
export interface UserAvatar {
  email: string;
  url: string;
  expiresAt: string;
}

/** email (lower-cased) → url. The api already lower-cases; this does not trust that. */
export function buildAvatarMap(list: readonly UserAvatar[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const a of list) if (a?.email && a.url) map.set(a.email.trim().toLowerCase(), a.url);
  return map;
}

/** Case-insensitive lookup; null for no avatar (→ initials). */
export function lookupAvatar(map: ReadonlyMap<string, string>, email: string | null | undefined): string | null {
  if (!email) return null;
  return map.get(email.trim().toLowerCase()) ?? null;
}

/** Slack past `expiresAt`, so the refetch lands in the next signing window, not the old one. */
export const REFRESH_SLACK_MS = 5_000;
/** Floor: an expiry already in the past (asleep tab, skewed clock) must not refetch in a tight loop. */
export const REFRESH_MIN_MS = 30_000;
/** No avatars at all → still look again hourly, so a teammate's new photo appears without a reload. */
export const REFRESH_IDLE_MS = 60 * 60 * 1000;

/** Epoch ms of the earliest parseable `expiresAt`, or null when there is none. */
export function earliestExpiry(list: readonly UserAvatar[]): number | null {
  let min: number | null = null;
  for (const a of list) {
    const t = Date.parse(a?.expiresAt ?? '');
    if (Number.isFinite(t) && (min === null || t < min)) min = t;
  }
  return min;
}

/** How long to wait before refetching `userAvatars`, given the current list and `now`. */
export function nextRefreshDelay(list: readonly UserAvatar[], now: number): number {
  const at = earliestExpiry(list);
  if (at === null) return REFRESH_IDLE_MS;
  return Math.max(REFRESH_MIN_MS, at + REFRESH_SLACK_MS - now);
}
